import { DateTime } from 'luxon'
import { ProfitData, ReserveFeeDevice, ScaleLabeling } from '../types/charts'
import { PROFIT_BAR_COUNT, PROFIT_MONTHLY_FEE } from '../types/constants'
import { SeriesOptions } from './series'

/**
 * Utility collection for profit calculations.
 */
export const Profits = {

  /**
   * Fill in actual profit to the data. Assume data in chronological order.
   */
  calculate: (data: ProfitData[], scale: ScaleLabeling): void => {

    if (data.length === 0) return

    let fee = 0.0

    switch (scale) {
      case 'hour':
        fee = PROFIT_MONTHLY_FEE / (30 * 24)
        break

      case 'day':
        fee = PROFIT_MONTHLY_FEE / 30
        break

      case 'week':
        fee = PROFIT_MONTHLY_FEE / (30 / 7)
        break

      case 'month':
        fee = PROFIT_MONTHLY_FEE
        break

      case 'year':
        fee = PROFIT_MONTHLY_FEE * 12
        break
    }

    data.forEach(p => {
      p.fcr_down_price = Math.max(0, p.fcr_down_price - fee)
      p.fcr_up_price = Math.max(0, p.fcr_up_price - fee)
    })
  },

  /**
   * Replace the reserve compensation of monthly entries with what is left after the
   * service fee, as one figure (carried in `fcr_up_price`; `fcr_down_price` zeroed).
   * Months are matched in the location's time zone; a month without a figure has none.
   */
  applyFees: (data: ProfitData[], chart: ReserveFeeDevice['chart'], tz: string): void => {
    const byMonth = new Map(chart.map(c => [c.month, c.payout]))

    data.forEach(p => {
      const month = DateTime.fromISO(p.timestamp, { zone: 'utc' }).setZone(tz).toFormat('yyyy-MM')
      p.fcr_up_price = byMonth.get(month) ?? 0
      p.fcr_up_power = 0
      p.fcr_down_price = 0
      p.fcr_down_power = 0
    })
  },

  /**
   * Whether any entry carries a solar value. The backend leaves `pv_value` out
   * when the location has no solar production, and then the chart shows no
   * solar bar, legend entry or note at all.
   */
  hasPvValue: (data: ProfitData[]): boolean => data.some(p => p.pv_value !== undefined && p.pv_value !== null),

  /**
   * Total profit of one entry: reserve compensations, self-use saving and, when present, the solar value.
   */
  total: (p: ProfitData): number => p.fcr_up_price + p.fcr_down_price + p.spot_saving + (p.pv_value ?? 0),

  /**
   * Pad profit data to fill the chart.
   *
   * The backend already returns exactly one entry per requested time step
   * (hour/day/week/month/year) in chronological order.  We just need to pad
   * the array so the chart always has at least PROFIT_BAR_COUNT bars and a
   * trailing buffer for smooth scrolling.
   */
  fillData: (data: ProfitData[], _options: SeriesOptions, empty: ProfitData): ProfitData[] => {
    const fullData: ProfitData[] = [...data]

    while (fullData.length < PROFIT_BAR_COUNT) {
      fullData.unshift(empty)
    }
    for (let i = 0; i < PROFIT_BAR_COUNT / 2 - 1; i++) {
      fullData.push(empty)
    }

    return fullData
  }
}
